import React from 'react';
import { Headphones } from 'lucide-react';
import { PLAYBACK_MODES, REPEAT_MODES } from '../../utils/buildPlaybackQueue';
import ErrorMessage from '../ErrorMessage';

const SPEEDS = [0.75, 1, 1.25, 1.5];

const ListenSettingsForm = ({
  localSettings,
  updateLocal,
  voice,
  setVoice,
  handleStart,
  stopPlayback,
  hasQueue,
  isLoading,
  error,
  speed = 1,
  setSpeed,
  variant = 'light'
}) => {
  const isDark = variant === 'dark';
  const labelClass = isDark ? 'spotify-label' : 'listen-label';
  const selectClass = isDark ? 'spotify-select' : 'listen-select';
  const titleClass = isDark
    ? 'text-lg font-sans font-semibold text-white mb-4 flex items-center gap-2 leading-tight'
    : 'text-lg font-sans font-semibold text-gita-maroon mb-4 flex items-center gap-2 leading-tight';

  return (
    <div className={isDark ? 'spotify-settings-form' : ''}>
      <h3 className={titleClass}>
        <Headphones className="w-5 h-5" />
        Session settings
      </h3>

      <div className="space-y-5">
        <div>
          <label className={labelClass} htmlFor="playback-mode">
            What to play
          </label>
          <select
            id="playback-mode"
            value={localSettings.mode}
            onChange={(e) => updateLocal({ mode: e.target.value })}
            className={selectClass}
          >
            {PLAYBACK_MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelClass} htmlFor="listen-language">
            Meaning language
          </label>
          <select
            id="listen-language"
            value={localSettings.language}
            onChange={(e) => updateLocal({ language: e.target.value })}
            className={selectClass}
          >
            <option value="te">Telugu</option>
            <option value="hi">Hindi</option>
            <option value="en">English</option>
          </select>
          {!isDark && (
            <p className="text-xs text-gray-500 mt-1">Used for meaning audio and sloka order</p>
          )}
        </div>

        <div>
          <label className={labelClass} htmlFor="listen-voice">
            Verse recitation voice
          </label>
          <select
            id="listen-voice"
            value={voice}
            onChange={(e) => setVoice(e.target.value)}
            className={selectClass}
          >
            <option value="male">Male voice</option>
            <option value="female">Female voice</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass} htmlFor="start-chapter">
              From chapter
            </label>
            <select
              id="start-chapter"
              value={localSettings.startChapter}
              onChange={(e) => updateLocal({ startChapter: parseInt(e.target.value, 10) })}
              className={selectClass}
            >
              {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass} htmlFor="end-chapter">
              To chapter
            </label>
            <select
              id="end-chapter"
              value={localSettings.endChapter}
              onChange={(e) => updateLocal({ endChapter: parseInt(e.target.value, 10) })}
              className={selectClass}
            >
              {Array.from({ length: 18 }, (_, i) => i + 1).map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass} htmlFor="repeat-mode">
            After last chapter
          </label>
          <select
            id="repeat-mode"
            value={localSettings.repeat}
            onChange={(e) => updateLocal({ repeat: e.target.value })}
            className={selectClass}
          >
            {REPEAT_MODES.map((m) => (
              <option key={m.id} value={m.id}>
                {m.label}
              </option>
            ))}
          </select>
        </div>

        {setSpeed && (
          <div>
            <label className={labelClass}>Playback speed</label>
            <div className="flex gap-2">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSpeed(s)}
                  className={[
                    'flex-1 py-1.5 rounded-lg text-sm font-medium border transition-colors',
                    speed === s
                      ? isDark
                        ? 'bg-gita-gold text-gita-maroon border-gita-gold'
                        : 'bg-gita-maroon text-white border-gita-maroon'
                      : isDark
                        ? 'bg-white/5 text-white/60 border-white/10 hover:bg-white/10'
                        : 'bg-gray-100 text-gray-600 border-gray-200 hover:bg-gray-200'
                  ].join(' ')}
                >
                  {s}×
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3 mt-6">
        <button
          type="button"
          onClick={handleStart}
          className={isDark ? 'spotify-btn-primary flex-1' : 'btn-primary flex-1 justify-center'}
          disabled={isLoading}
        >
          {hasQueue ? 'Restart session' : 'Start listening'}
        </button>
        {hasQueue && (
          <button
            type="button"
            onClick={stopPlayback}
            className={isDark ? 'spotify-btn-secondary flex-1' : 'btn-secondary flex-1 justify-center'}
          >
            Stop
          </button>
        )}
      </div>

      {error && (
        <div className="mt-4">
          <ErrorMessage message={error} onRetry={handleStart} />
        </div>
      )}
    </div>
  );
};

export default ListenSettingsForm;
